import { Inject, Injectable } from '@angular/core';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders
} from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, firstValueFrom, Observable, Subject, throwError, of, timer } from 'rxjs';
import { catchError, finalize, retry, map, shareReplay } from 'rxjs/operators';
import { globalData } from './globaldata';
import { AuthStateService } from './auth_state.service';
import { SessionTimeoutService } from './session_timeout.service';
import { ProductLicensesService } from './product_licenses.service';
import { MerchantsService } from './merchants.service';
import { RUNTIME_ENV } from '../tokens/runtime_env.token';

export interface RegisterResponse {
    message: string;
    success: boolean;
    token: string;
    session_id: string;
    session_timeout_minutes: number;
    [key: string]: any;
}

interface SessionMeta {
    sessionId: string | null;
    timeoutMinutes: number;
    expiresAt: number | null;
}

interface RememberLoginResponse extends RegisterResponse {
    remember_token?: string | null;
    remember_expires_in_days?: number | null;
}

@Injectable({ providedIn: 'root' })
export class LoginService {
    private static readonly SUPER_ADMIN_USER_IDS: ReadonlySet<number> = new Set([1, 2, 3, 4]);

    static readonly DEFAULT_HOME_ROUTE = '/dashboard/home';
    static readonly FILE_UPLOADER_HOME_ROUTE = '/secure-storage/dashboard';

    private authToken: string | null = null;
    private user: any = null;
    private readonly url = environment.baseurl;
    private readonly sessionKey = 'cb4.sessionMeta';
    private readonly rememberTokenKey = 'cb4.rememberToken';
    private readonly defaultTimeoutMinutes = 15;
    private readonly appEnvironment: string;
    private readonly bypassLoginEnabled: boolean;
    private sessionMeta: SessionMeta = {
        sessionId: null,
        timeoutMinutes: this.defaultTimeoutMinutes,
        expiresAt: null
    };
    private sessionEnded$ = new Subject<{ method: 'BUTTON' | 'INACTIVITY' | 'TIMEOUT'; message?: string }>();
    private bypassLogin$: Observable<boolean> | null = null;
    private rememberRestore$: Observable<boolean> | null = null;
    private rememberRestoreToken: string | null = null;
    private rememberedSessionRestorePromise: Promise<boolean> | null = null;
    private sessionTimeoutPromise: Promise<void> | null = null;
    private userSubject = new BehaviorSubject<any>(null);
    private hasStorageSyncListener = false;

    constructor(
        private http: HttpClient,
        private globalData: globalData,
        private authState: AuthStateService,
    private sessionTimeout: SessionTimeoutService,
    private productLicenses: ProductLicensesService,
    private merchantsService: MerchantsService,
    @Inject(RUNTIME_ENV) private runtimeEnv: any
) {
        this.appEnvironment = this.resolveAppEnvironment();
        this.bypassLoginEnabled = this.resolveBypassLoginEnabled();
        this.setupStorageSync();
        this.loadFromStorage();
    }

    get sessionEnded() {
        return this.sessionEnded$.asObservable();
    }

    isBypassLoginEnabled(): boolean {
        return this.bypassLoginEnabled;
    }

    getUserUpdates(): Observable<any> {
        return this.userSubject.asObservable();
    }

    getUserSnapshot(): any {
        return this.user;
    }

    ensureBypassLogin(): Observable<boolean> {
        if (!this.bypassLoginEnabled) {
            return of(false);
        }

        if (this.isSessionValid()) {
            return of(true);
        }

        if (this.bypassLogin$) {
            return this.bypassLogin$;
        }

        const request$ = this.http
            .post<RegisterResponse>(`${this.url}login/bypass`, {}, {
                headers: this.getDefaultHeaders(),
            })
            .pipe(
                map((response) => {
                    if (response?.success && response.token) {
                        this.storeUserData(response.token, response);
                        return true;
                    }
                    return false;
                }),
                catchError((error: HttpErrorResponse) => {
                    console.error('Automatic login failed.', error);
                    return of(false);
                }),
                finalize(() => {
                    this.bypassLogin$ = null;
                })
            );

        this.bypassLogin$ = request$.pipe(shareReplay(1));
        return this.bypassLogin$;
    }

    loginWithRememberToken(rememberToken: string): Observable<RememberLoginResponse> {
        return this.http.post<RememberLoginResponse>(`${this.url}login/remember`, {
            remember_token: rememberToken
        }, {
            headers: this.getDefaultHeaders(),
        }).pipe(
            catchError((error: HttpErrorResponse) => throwError(() => error))
        );
    }

    private resolveBypassLoginEnabled(): boolean {
        const sourceValue =
            this.runtimeEnv?.BYPASS_LOGIN ??
            this.runtimeEnv?.bypassLogin ??
            (environment as any).BYPASS_LOGIN ??
            environment.bypassLogin;

        const bypassEnabled = this.normalizeBooleanFlag(sourceValue, false);

        return this.appEnvironment === 'local' && bypassEnabled;
    }

    private resolveAppEnvironment(): string {
        const sourceValue =
            this.runtimeEnv?.APP_ENV ??
            this.runtimeEnv?.appEnv ??
            (environment as any).APP_ENV ??
            environment.appEnv ??
            '';

        if (typeof sourceValue === 'string') {
            return sourceValue.trim().toLowerCase();
        }

        if (sourceValue === null || sourceValue === undefined) {
            return '';
        }

        return String(sourceValue).trim().toLowerCase();
    }

    private normalizeBooleanFlag(value: any, defaultValue: boolean): boolean {
        if (value === undefined || value === null) {
            return defaultValue;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        const normalized = String(value).trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalized)) {
            return true;
        }
        if (['false', '0', 'no', 'off'].includes(normalized)) {
            return false;
        }

        return defaultValue;
    }

    authenticateUser(user: any): Observable<RegisterResponse> {
        return this.http
            .post<RegisterResponse>(`${this.url}login`, user, {
                headers: this.getDefaultHeaders(),
            })
            .pipe(
                retry({
                    count: 1,
                    delay: (error: HttpErrorResponse) => {
                        if (error.status === 0 || error.status >= 500) {
                            return timer(250);
                        }

                        throw error;
                    },
                }),
                catchError((error: HttpErrorResponse) => throwError(() => error))
            );
    }

    requestPasswordReset(payload: { email: string }): Observable<any> {
        return this.http.post(`${this.url}password-reset/request`, payload, {
            headers: this.getDefaultHeaders(),
        });
    }

    verifyPasswordResetToken(payload: { token: string }): Observable<any> {
        return this.http.post(`${this.url}password-reset/verify`, payload, {
            headers: this.getDefaultHeaders(),
        });
    }

    completePasswordReset(payload: { token: string | null; password: string }): Observable<any> {
        return this.http.post(`${this.url}password-reset/complete`, payload, {
            headers: this.getDefaultHeaders(),
        });
    }

    resendActivationInvite(payload: { token: string | null }): Observable<any> {
        return this.http.post(`${this.url}password-reset/resend-invite`, payload, {
            headers: this.getDefaultHeaders(),
        });
    }

    storeUserData(token: string, response: any): void {
        const timeoutMinutes = this.normalizeTimeout(response.session_timeout_minutes);
        const user = { ...response, session_timeout_minutes: timeoutMinutes };
        const rememberToken = typeof response?.remember_token === 'string' ? response.remember_token.trim() : '';

        this.writeStorageItem('user', JSON.stringify(user));
        this.writeStorageItem('id_token', token);
        if (rememberToken) {
            this.writeStorageItem(this.rememberTokenKey, rememberToken);
        }

        this.authToken = token;
        this.user = user;

        const expiresAt = this.computeExpiry(timeoutMinutes);
        this.sessionMeta = {
            sessionId: response.session_id || null,
            timeoutMinutes,
            expiresAt,
        };

        this.writeStorageItem(this.sessionKey, JSON.stringify(this.sessionMeta));
        this.productLicenses.clearCache();
        this.merchantsService.clearCache();
        this.updateGlobalData(user);
        this.publishUser();
        if (timeoutMinutes > 0) {
            this.sessionTimeout.start(timeoutMinutes, () =>
                void this.handleSessionTimeout('INACTIVITY')
            );
        } else {
            this.sessionTimeout.stop();
        }

        void this.productLicenses.loadLicenses(true);
        void this.merchantsService.loadMerchants(true);
    }

    updateCurrentUser(partial: Record<string, unknown>): void {
        if (!partial || typeof partial !== 'object') {
            return;
        }

        if (!this.user) {
            return;
        }

        this.user = { ...this.user, ...partial };
        this.writeStorageItem('user', JSON.stringify(this.user));
        this.updateGlobalData(this.user);
        this.publishUser();
    }

    markSessionActivity(): void {
        if (!this.isLoggedIn()) {
            return;
        }

        if (this.sessionMeta.timeoutMinutes > 0) {
            this.sessionMeta.expiresAt = this.computeExpiry(this.sessionMeta.timeoutMinutes);
            this.writeStorageItem(this.sessionKey, JSON.stringify(this.sessionMeta));
            this.sessionTimeout.notifyExternalActivity();
        }
    }

    getCurrentUserDisplayName(): string | null {
        const candidates = [
            this.user?.display_name,
            this.user?.displayName,
            this.user?.user_name,
            this.user?.userName
        ];

        for (const candidate of candidates) {
            if (typeof candidate === 'string') {
                const trimmed = candidate.trim();
                if (trimmed) {
                    return trimmed;
                }
            }
        }

        if (typeof this.user?.email === 'string') {
            const email = this.user.email.trim();
            return email || null;
        }

        return null;
    }

    getToken(): string | null {
        return this.authToken;
    }

    getCurrentUserId(): number | null {
        const candidates = [this.user?.user_id, this.user?.userId, this.user?.id];
        for (const candidate of candidates) {
            const numeric = Number(candidate);
            if (Number.isInteger(numeric) && numeric > 0) {
                return numeric;
            }
        }

        return null;
    }

    getCurrentAccountId(): number | null {
        const accountId = Number(this.user?.account_id ?? this.user?.accountId);
        if (Number.isInteger(accountId) && accountId > 0) {
            return accountId;
        }

        return null;
    }

    getCurrentUserRole(): string | null {
        const rawRole =
            (this.user && typeof this.user.role === "string" ? this.user.role : null) ??
            (typeof (this.user as { user_role?: unknown })?.user_role === "string"
                ? (this.user as { user_role?: string }).user_role
                : null);

        if (!rawRole) {
            return null;
        }

        const trimmed = rawRole.trim();
        return trimmed.length > 0 ? trimmed : null;
    }

    isFileUploader(user?: { role?: unknown; user_role?: unknown } | null): boolean {
        const roleValue = user
            ? (typeof user.role === 'string' ? user.role : typeof user.user_role === 'string' ? user.user_role : null)
            : this.getCurrentUserRole();
        return this.normalizeRole(roleValue) === 'storage only';
    }

    getDefaultAuthenticatedRoute(user?: { role?: unknown; user_role?: unknown } | null): string {
        return this.isFileUploader(user) ? LoginService.FILE_UPLOADER_HOME_ROUTE : LoginService.DEFAULT_HOME_ROUTE;
    }

    resolvePostLoginRoute(
        requestedRoute: string | null | undefined,
        user?: { role?: unknown; user_role?: unknown } | null
    ): string {
        const defaultRoute = this.getDefaultAuthenticatedRoute(user);
        const normalizedRequestedRoute = typeof requestedRoute === 'string' ? requestedRoute.trim() : '';
        if (!normalizedRequestedRoute) {
            return defaultRoute;
        }

        if (this.isFileUploader(user) && normalizedRequestedRoute.toLowerCase().startsWith('/dashboard')) {
            return defaultRoute;
        }

        return normalizedRequestedRoute;
    }

    hasAdminPrivileges(): boolean {
        const normalizedRole = this.normalizeRole(this.getCurrentUserRole());
        if (!normalizedRole) {
            return false;
        }

        return normalizedRole === "admin" || normalizedRole === "global admin";
    }

    isGlobalAdmin(): boolean {
        const normalizedRole = this.normalizeRole(this.getCurrentUserRole());
        if (!normalizedRole) {
            return false;
        }

        return normalizedRole === "global admin";
    }

    isSuperAdmin(): boolean {
        return LoginService.SUPER_ADMIN_USER_IDS.has(this.getCurrentUserId() ?? 0);
    }

    canSwitchAccounts(): boolean {
        return this.isSuperAdmin();
    }

    isLoggedIn(): boolean {
        return !!this.authToken && !!this.user;
    }

    isSessionValid(): boolean {
        if (!this.isLoggedIn()) {
            return false;
        }

        if (
            this.sessionMeta.timeoutMinutes > 0 &&
            this.sessionMeta.expiresAt &&
            Date.now() > this.sessionMeta.expiresAt
        ) {
            void this.handleSessionTimeout('TIMEOUT');
            return false;
        }

        return true;
    }

    logout(method: 'BUTTON' | 'INACTIVITY' | 'TIMEOUT' = 'BUTTON'): Observable<void> {
        if (!this.isLoggedIn()) {
            if (method === 'BUTTON') {
                this.removeStorageItem(this.rememberTokenKey);
            }
            this.finishSessionEnd(method, 'You have been logged out.');
            return of(void 0);
        }

        const token = this.authToken;
        return this.http
            .post<void>(`${this.url}logout`, { method }, { headers: this.getAuthHeaders() })
            .pipe(
                catchError((error: HttpErrorResponse) => throwError(() => error)),
                finalize(() => {
                    if (this.authToken !== token) return;
                    if (method === 'BUTTON') {
                        this.removeStorageItem(this.rememberTokenKey);
                    }
                    this.finishSessionEnd(method, 'You have been logged out.');
                })
            );
    }

    handleSessionTimeout(method: 'INACTIVITY' | 'TIMEOUT'): Promise<void> {
        if (this.sessionTimeoutPromise) {
            return this.sessionTimeoutPromise;
        }

        // Set the shared promise before session notifications can re-enter this flow.
        const expiredToken = this.authToken;
        this.sessionTimeoutPromise = Promise.resolve()
            .then(() => {
                if (this.authToken === expiredToken) return this.endExpiredSession(method);
            })
            .finally(() => { this.sessionTimeoutPromise = null; });
        return this.sessionTimeoutPromise;
    }

    private async endExpiredSession(method: 'INACTIVITY' | 'TIMEOUT'): Promise<void> {
        const expiredToken = this.authToken;
        if (method === 'INACTIVITY') {
            this.authState.setInactivityRedirect(this.authState.getCurrentUrl());
        }
        const message =
            method === 'INACTIVITY'
                ? 'Your session ended due to inactivity.'
                : 'Your session has expired. Please log in again.';

        const remembered = await this.tryRememberedSessionRestore();
        if (this.authToken && this.authToken !== expiredToken) {
            return;
        }
        if (remembered) {
            this.errorOutOnExpiredSessionIfNeeded();
            return;
        }

        const token = this.getToken();
        if (!token) {
            this.finishSessionEnd(method, message);
            return;
        }

        await firstValueFrom(this.http
            .post<void>(`${this.url}logout`, { method }, { headers: this.getAuthHeaders() })
            .pipe(
                catchError(() => of(void 0)),
                finalize(() => {
                    if (this.authToken === token) this.finishSessionEnd(method, message);
                })
            ));
    }

    completeAccountRemoval(message: string = 'Your account has been deactivated.'): void {
        this.authState.clearAllRedirects();
        this.finishSessionEnd('BUTTON', message);
    }

    private loadFromStorage(): void {
        // Storage hydration owns notifications; getters must never publish/re-enter listeners.
        this.authToken = this.readStorageItem('id_token');
        const storedUser = this.readStorageItem('user');
        try {
            this.user = storedUser ? JSON.parse(storedUser) : null;
        } catch {
            this.user = null;
        }
        if (!this.authToken || !this.user || typeof this.user !== 'object' || Array.isArray(this.user)) {
            this.authToken = null;
            this.user = null;
        }

        this.sessionMeta = this.parseSessionMeta(this.readStorageItem(this.sessionKey));

        if (this.user) {
            this.updateGlobalData(this.user);
            if (this.sessionMeta.timeoutMinutes > 0) {
                this.sessionTimeout.start(this.sessionMeta.timeoutMinutes, () =>
                    void this.handleSessionTimeout('INACTIVITY')
                );
            } else {
                this.sessionTimeout.stop();
            }
            this.publishUser();
        } else {
            this.sessionTimeout.stop();
            this.updateGlobalData({});
            this.publishUser();
        }
    }

    private clearSession(): void {
        this.sessionTimeout.stop();
        this.removeStorageItem('user');
        this.removeStorageItem('id_token');
        this.removeStorageItem(this.sessionKey);
        this.authToken = null;
        this.user = null;
        this.sessionMeta = {
            sessionId: null,
            timeoutMinutes: this.defaultTimeoutMinutes,
            expiresAt: null
        };
        this.updateGlobalData({});
        this.productLicenses.clearCache();
        this.merchantsService.clearCache();
        this.publishUser();
    }

    private normalizeTimeout(value: any): number {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            return this.defaultTimeoutMinutes;
        }
        if (parsed < 0) {
            return this.defaultTimeoutMinutes;
        }
        return parsed;
    }

    private computeExpiry(minutes: number): number | null {
        const normalized = this.normalizeTimeout(minutes);
        if (normalized <= 0) {
            return null;
        }
        return Date.now() + normalized * 60 * 1000;
    }

    private getDefaultHeaders(): HttpHeaders {
        return new HttpHeaders({ 'Content-Type': 'application/json' });
    }

    private getAuthHeaders(): HttpHeaders {
        const token = this.getToken();
        if (!token) {
            return this.getDefaultHeaders();
        }
        return this.getDefaultHeaders().set('Authorization', `Bearer ${token}`);
    }

    private updateGlobalData(user: any): void {
        const accountName = this.resolveAccountName(user);
        Object.assign(this.globalData, {
            email: user?.email ?? null,
            user_name: user?.user_name ?? null,
            company_name: accountName,
            account_name: accountName,
            accountName,
            user_id: user?.user_id ?? null,
            account_id: user?.account_id ?? null,
            role: user?.role ?? null,
            category: user?.category ?? null,
        });
        this.globalData.userId = this.globalData.user_id;
    }

    private resolveAccountName(user: any): string | null {
        const candidates = [user?.company_name, user?.account_name, user?.accountName];
        for (const candidate of candidates) {
            if (typeof candidate === 'string') {
                const normalized = candidate.trim();
                if (normalized) {
                    return normalized;
                }
            }
        }

        return null;
    }

    private finishSessionEnd(method: 'BUTTON' | 'INACTIVITY' | 'TIMEOUT', message: string): void {
        this.clearSession();
        this.sessionEnded$.next({ method, message });
    }

    private publishUser(): void {
        this.userSubject.next(this.user);
    }

    private setupStorageSync(): void {
        if (typeof window === 'undefined' || this.hasStorageSyncListener) {
            return;
        }

        window.addEventListener('storage', (event: StorageEvent) => {
            if (!event.key || !['user', 'id_token', this.sessionKey, this.rememberTokenKey].includes(event.key)) {
                return;
            }

            if (event.key === this.sessionKey && event.newValue !== null) {
                this.sessionMeta = this.parseSessionMeta(event.newValue);
                this.sessionTimeout.notifyExternalActivity();
                return;
            }

            const wasLoggedIn = this.isLoggedIn();
            this.loadFromStorage();
            if (wasLoggedIn && !this.isLoggedIn()) {
                this.sessionEnded$.next({ method: 'TIMEOUT', message: 'Your session ended in another tab.' });
            }
        });

        this.hasStorageSyncListener = true;
    }

    private parseSessionMeta(value: string | null): SessionMeta {
        if (value) {
            try {
                const parsed = JSON.parse(value) ?? {};
                const timeoutMinutes = this.normalizeTimeout(parsed.timeoutMinutes);
                const rawExpiresAt = Number(parsed.expiresAt);
                return {
                    sessionId: parsed.sessionId ?? null,
                    timeoutMinutes,
                    expiresAt: timeoutMinutes > 0 && Number.isFinite(rawExpiresAt) ? rawExpiresAt : null
                };
            } catch (error) {
                // Keep the normal timeout when stored session metadata is invalid.
            }
        }
        return { sessionId: null, timeoutMinutes: this.defaultTimeoutMinutes, expiresAt: null };
    }

    private readStorageItem(key: string): string | null {
        const storage = this.getLocalStorage();
        const value = storage?.getItem(key);
        if (value !== null) {
            return value;
        }

        const legacyValue = this.getSessionStorage()?.getItem(key) ?? null;
        if (legacyValue !== null) {
            this.writeStorageItem(key, legacyValue);
            this.getSessionStorage()?.removeItem(key);
        }

        return legacyValue;
    }

    private writeStorageItem(key: string, value: string): void {
        this.getLocalStorage()?.setItem(key, value);
    }

    private removeStorageItem(key: string): void {
        this.getLocalStorage()?.removeItem(key);
        this.getSessionStorage()?.removeItem(key);
    }

    private getLocalStorage(): Storage | null {
        return typeof window !== 'undefined' ? window.localStorage : null;
    }

    private getSessionStorage(): Storage | null {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    }

    private normalizeRole(value: unknown): string {
        if (typeof value !== 'string') {
            return '';
        }

        return value.trim().toLowerCase();
    }

    hasRememberToken(): boolean {
        const token = this.readStorageItem(this.rememberTokenKey);
        return typeof token === 'string' && token.trim().length > 0;
    }

    restoreSessionFromRememberToken(): Observable<boolean> {
        const rememberToken = this.readStorageItem(this.rememberTokenKey);
        if (!rememberToken) {
            return of(false);
        }

        if (this.rememberRestore$ && this.rememberRestoreToken === rememberToken) {
            return this.rememberRestore$;
        }

        this.rememberRestoreToken = rememberToken;
        const restoringToken = this.authToken;
        this.rememberRestore$ = this.loginWithRememberToken(rememberToken).pipe(
            map((response) => {
                if (this.authToken !== restoringToken) {
                    return this.isSessionValid();
                }
                if (!response?.success || !response?.token) {
                    this.removeStorageItem(this.rememberTokenKey);
                    return false;
                }

                this.storeUserData(response.token, response);
                return true;
            }),
            catchError(() => {
                if (this.authToken === restoringToken && this.readStorageItem(this.rememberTokenKey) === rememberToken) {
                    this.removeStorageItem(this.rememberTokenKey);
                }
                return of(false);
            }),
            finalize(() => {
                this.rememberRestore$ = null;
                this.rememberRestoreToken = null;
            }),
            shareReplay(1)
        );

        return this.rememberRestore$;
    }

    private async tryRememberedSessionRestore(): Promise<boolean> {
        if (!this.hasRememberToken()) {
            return false;
        }

        if (this.rememberedSessionRestorePromise) {
            return this.rememberedSessionRestorePromise;
        }

        this.clearSession();
        this.rememberedSessionRestorePromise = firstValueFrom(
            this.restoreSessionFromRememberToken()
        ).then(Boolean).catch(() => false);

        try {
            return await this.rememberedSessionRestorePromise;
        } finally {
            this.rememberedSessionRestorePromise = null;
        }
    }

    private errorOutOnExpiredSessionIfNeeded(): void {
        if (this.sessionMeta.timeoutMinutes > 0) {
            this.sessionMeta.expiresAt = this.computeExpiry(this.sessionMeta.timeoutMinutes);
            this.writeStorageItem(this.sessionKey, JSON.stringify(this.sessionMeta));
        }
    }
}
